import { Clock3, MapPinPlus } from 'lucide-react';
import type { ParadaDetalle, ParadaNoPlanificadaDetalle } from '@tms/contratos';
import { fmtDuracionMin, fmtFechaHora, fmtNum } from '../util/format';
import { ChipEstado } from './ChipEstado';
import { MOTIVO_NO_PLANIFICADA, TIPO_PARADA } from './estados';

function fila(etiqueta: string, valor: string, clase?: string) {
  return (
    <div>
      <span>{etiqueta}</span>
      <span className={clase}>{valor}</span>
    </div>
  );
}

/** "10 a 30 min", "hasta 30 min", "desde 10 min" o "—". */
export function permanenciaEsperada(min: number | null, max: number | null): string {
  if (min !== null && max !== null) return `${fmtDuracionMin(min)} a ${fmtDuracionMin(max)}`;
  if (max !== null) return `hasta ${fmtDuracionMin(max)}`;
  if (min !== null) return `desde ${fmtDuracionMin(min)}`;
  return '—';
}

type Item = { tipo: 'plan'; p: ParadaDetalle } | { tipo: 'noplan'; n: ParadaNoPlanificadaDetalle };

/** Hora de referencia de una parada de la ruta: llegada por GPS, o aviso de Mara, o primera detección. */
const referencia = (p: ParadaDetalle): string | null => p.llegadaReal ?? p.declaradaEn ?? p.detectadaEn;

/**
 * Intercala las paradas no planificadas entre las de la ruta según la hora: cada una va después de la
 * última parada alcanzada antes de su hora y antes de la primera parada pendiente.
 */
export function intercalar(paradas: ParadaDetalle[], noPlan: ParadaNoPlanificadaDetalle[]): Item[] {
  const pendientesNp = [...noPlan].sort((a, b) => a.fechaHora.localeCompare(b.fechaHora));
  const out: Item[] = [];
  const volcar = (hasta: string | null) => {
    while (pendientesNp.length && (hasta === null || pendientesNp[0].fechaHora < hasta)) out.push({ tipo: 'noplan', n: pendientesNp.shift()! });
  };
  for (const p of [...paradas].sort((a, b) => a.orden - b.orden)) {
    const ref = referencia(p);
    if (ref) volcar(ref);
    else if (p.estado === 'PENDIENTE') volcar(null);
    out.push({ tipo: 'plan', p });
  }
  volcar(null);
  return out;
}

function ItemPlanificado({ p, proxima }: { p: ParadaDetalle; proxima: boolean }) {
  const TipoIcono = TIPO_PARADA[p.tipo].icono;
  const excesoMin = p.permanenciaMin !== null && p.duracionMaxEsperadaMin !== null ? p.permanenciaMin - p.duracionMaxEsperadaMin : null;
  const cant = p.cantidadEntregada === null ? null : `${fmtNum(p.cantidadEntregada)} ${p.unidadMedida ?? ''}`.trim();
  return (
    <li className={`lt-item ${p.estado} ${proxima ? 'proxima' : ''}`}>
      <span className="lt-dot" aria-hidden>
        {p.orden}
      </span>
      <div className="lt-top">
        <span>
          <strong>{p.nombre}</strong>
          <small className="lt-tipo">
            <TipoIcono aria-hidden />
            {TIPO_PARADA[p.tipo].etiqueta}
            {p.obligatoria ? ' · obligatoria' : ' · opcional'}
          </small>
        </span>
        <span style={{ display: 'inline-flex', gap: 6 }}>
          {proxima && <span className="chip green">Próxima</span>}
          <ChipEstado tipo="parada" valor={p.estado} />
        </span>
      </div>
      <div className="lt-cols">
        <div>
          <h4>Planificado</h4>
          {fila('Permanencia', permanenciaEsperada(p.duracionMinEsperadaMin, p.duracionMaxEsperadaMin))}
          {fila('ETA actual', p.estado === 'PENDIENTE' ? fmtFechaHora(p.etaActual) : '—')}
          {fila('Radio', `${fmtNum(p.radioM)} m`)}
        </div>
        <div>
          <h4>Real</h4>
          {fila('Llegada (GPS)', fmtFechaHora(p.llegadaReal))}
          {fila('Salida (GPS)', fmtFechaHora(p.salidaReal))}
          {fila('Permanencia', fmtDuracionMin(p.permanenciaMin), excesoMin !== null && excesoMin > 0 ? 'amber' : undefined)}
          {fila('Declarada por Mara', fmtFechaHora(p.declaradaEn))}
        </div>
      </div>
      {(cant || p.observacion || (excesoMin !== null && excesoMin > 0)) && (
        <div className="lt-extra">
          {cant && (
            <span>
              Entregado: <b>{cant}</b>
            </span>
          )}
          {excesoMin !== null && excesoMin > 0 && (
            <span className="retraso">
              <Clock3 aria-hidden />+{fmtDuracionMin(excesoMin)} sobre la permanencia máxima
            </span>
          )}
          {p.observacion && <span className="lt-obs">«{p.observacion}»</span>}
        </div>
      )}
    </li>
  );
}

function ItemNoPlanificado({ n }: { n: ParadaNoPlanificadaDetalle }) {
  return (
    <li className="lt-item NOPLAN">
      <span className="lt-dot" aria-hidden>
        <MapPinPlus />
      </span>
      <div className="lt-top">
        <span>
          <strong>Parada no planificada</strong>
          <small>{MOTIVO_NO_PLANIFICADA[n.motivo]}</small>
        </span>
        <span className="chip amber">
          <MapPinPlus aria-hidden />
          Declarada por el chofer
        </span>
      </div>
      <div className="lt-cols">
        <div>
          <h4>Aviso de Mara</h4>
          {fila('Declarada', fmtFechaHora(n.fechaHora))}
        </div>
        <div>
          <h4>GPS</h4>
          {fila('Detención', n.detencionId !== null ? 'La confirma' : 'No se vio', n.detencionId !== null ? 'green' : 'amber')}
        </div>
      </div>
      {n.observacion && (
        <div className="lt-extra">
          <span className="lt-obs">«{n.observacion}»</span>
        </div>
      )}
    </li>
  );
}

interface Props {
  paradas: ParadaDetalle[];
  noPlanificadas?: ParadaNoPlanificadaDetalle[];
  /** Texto cuando no hay paradas (p. ej. viaje sin ruta). */
  vacio?: string;
}

/** Línea de tiempo vertical de las paradas de la ruta, planificado contra real, con las no planificadas intercaladas. */
export function LineaTiempoParadas({ paradas, noPlanificadas = [], vacio = 'La ruta del viaje no tiene paradas.' }: Props) {
  const proxima = paradas.find((p) => p.estado === 'PENDIENTE')?.id;
  const items = intercalar(paradas, noPlanificadas);
  if (items.length === 0) return <div className="vacio">{vacio}</div>;
  return (
    <ol className="lt" aria-label="Paradas del viaje">
      {items.map((it) =>
        it.tipo === 'plan' ? <ItemPlanificado key={`p-${it.p.id}`} p={it.p} proxima={it.p.id === proxima} /> : <ItemNoPlanificado key={`n-${it.n.id}`} n={it.n} />,
      )}
    </ol>
  );
}
